/* eslint-disable @typescript-eslint/no-explicit-any */
import { SpatialFilter, WidgetSource } from '@carto/api-client'
import { Layer, LAYER_VIS_CONFIGS } from '@carto/kepler.gl/layers'
import { SCALE_TYPES, AGGREGATION_TYPES, CHANNEL_SCALES } from '@carto/kepler.gl/constants'
import { checkColorOrFallback } from '@carto/kepler.gl/utils'
import { TILE_FORMATS } from '@carto/react-core'
import {
  getAttributesFromTilesetDataset,
  getBoundsFromTilesetDataset,
  getGeometryFromTilesetDataset,
  getDatasetType,
  attributeHasStats,
  getSchemeFromTilesetDataset,
  getTilejsonFromTilesetDataset,
  getSpatialIndexFromGeoColumn,
  getHexColumnStatsFromTilesetDataset
} from 'features/builder/state/builderSliceUtils'
import { LAYER_HIGHLIGHT_FILL_COLOR_HOVER } from './constants'
import IconTilesets from 'assets/icons/layers/tilesets.svg?react'
import { GeomType, MapType } from 'features/maps/types'
import { getLegendSettings } from 'features/builder/api/builderQueries'
import { getDynamicTileResolution } from 'features/common/utils/cartoDeckGL'
import {
  GeometryType,
  LayerTypes,
  LayerType,
  KeplerColorRange,
  KeplerLayer,
  KeplerLayerConfig,
  KeplerLayerMeta,
  KeplerTable,
  KeplerTableField,
  KeplerVisualChannel,
  KeplerScaleType
} from 'features/builder/types'
import { createNewLayerColorGenerator } from './colorUtils'
import { colorMapsEqual, getLog10ScaleColorMap } from '../../layers/colorScaleUtils'

const noneLayerDataAffectingProps = ['label', 'opacity', 'thickness', 'isVisible', 'hidden', 'isConfigActive']
const noneLayerDataAffectingPropsWithOpacityForChannel = noneLayerDataAffectingProps.filter(
  (prop) => prop !== 'opacity'
)

export const tilesetVisConfigs = {
  opacity: 'opacity',
  strokeOpacity: {
    ...LAYER_VIS_CONFIGS.opacity,
    property: 'strokeOpacity'
  },
  thickness: {
    ...LAYER_VIS_CONFIGS.thickness,
    defaultValue: 1
  },
  strokeColor: 'strokeColor',
  colorRange: 'colorRange',
  strokeColorRange: 'strokeColorRange',
  radius: 'radius',
  sizeRange: 'strokeWidthRange',
  radiusRange: {
    ...LAYER_VIS_CONFIGS.radiusRange,
    range: [0, 100]
  },
  customMarkerSize: {
    ...LAYER_VIS_CONFIGS.radius,
    range: [0, 200]
  },
  customMarkerSizeRange: {
    ...LAYER_VIS_CONFIGS.radiusRange,
    range: [0, 200]
  },
  heightRange: 'elevationRange',
  elevationScale: 'elevationScale',
  stroked: 'stroked',
  filled: 'filled',
  customMarkers: 'customMarkers',
  customMarkersUrl: 'customMarkersUrl',
  customMarkersId: {
    type: 'string',
    defaultValue: undefined
  },
  customMarkersRange: 'customMarkersRange',
  enable3d: 'enable3d',
  wireframe: 'wireframe',
  initialStrokeColor: {
    type: 'color',
    defaultValue: null
  },
  colorAggregation: 'colorAggregation',
  strokeColorAggregation: {
    type: 'string',
    defaultValue: null
  },
  radiusAggregation: {
    type: 'string',
    defaultValue: null
  },
  sizeAggregation: {
    type: 'string',
    defaultValue: null
  }
}

export const defaultElevation = 500
export const defaultLineWidth = 1
export const defaultRadius = 1

const strokedGeometries = [
  GeometryType.Polygon,
  GeometryType.MultiPolygon,
  GeometryType.Line,
  GeometryType.LineString,
  GeometryType.MultiLineString
]

const numericFieldTypes = ['number', 'integer', 'real', 'float']
export default class TileLayer extends Layer {
  [x: string]: any
  static type: string
  spatialIndexResolution?: number
  declare meta: KeplerLayerMeta
  // @ts-expect-error TODO: Resolve type differences.
  declare config: KeplerLayerConfig

  constructor(props = {}) {
    super(props)
    this.registerVisConfig(tilesetVisConfigs)
  }

  public get type(): LayerType {
    return LayerTypes.Tileset
  }

  get name() {
    return 'Tiles'
  }

  get layerIcon() {
    return IconTilesets
  }

  get canDropFeatures() {
    return true
  }

  get visualChannels(): any {
    const visualChannels = super.visualChannels
    return {
      color: {
        ...visualChannels.color,
        opacityProperty: 'opacity',
        accessor: 'getFillColor',
        condition: (config: any) => config.visConfig.filled,
        nullValue: visualChannels.color.nullValue,
        // defaultValue must be static.
        getAttributeValue: (config: any) => this.getChannelColorValue(config.color, config.visConfig.opacity),
        defaultValue: (config: any) => config.color,
        aggregation: 'colorAggregation'
      },
      strokeColor: {
        property: 'strokeColor',
        opacityProperty: 'strokeOpacity',
        field: 'strokeColorField',
        scale: 'strokeColorScale',
        domain: 'strokeColorDomain',
        range: 'strokeColorRange',
        key: 'strokeColor',
        channelScaleType: CHANNEL_SCALES.color,
        accessor: 'getLineColor',
        condition: (config: any) => config.visConfig.stroked,
        nullValue: visualChannels.color.nullValue,
        // defaultValue must be static.
        getAttributeValue: (config: any) =>
          this.getChannelColorValue(
            config.visConfig.strokeColor || config.color,
            config.visConfig.strokeOpacity ?? config.visConfig.opacity
          ),
        defaultValue: (config: any) => config.visConfig.strokeColor || config.color,
        aggregation: 'strokeColorAggregation'
      },
      size: {
        ...visualChannels.size,
        property: 'stroke',
        accessor: 'getLineWidth',
        condition: (config: any) => config.visConfig.stroked,
        nullValue: 0,
        getAttributeValue: () => (d: any) => d.properties.lineWidth || defaultLineWidth,
        defaultValue: () => defaultLineWidth,
        supportedFieldTypes: numericFieldTypes,
        aggregation: 'sizeAggregation'
      },
      height: {
        property: 'height',
        field: 'heightField',
        scale: 'heightScale',
        domain: 'heightDomain',
        range: 'heightRange',
        key: 'height',
        channelScaleType: CHANNEL_SCALES.size,
        accessor: 'getElevation',
        condition: (config: any) => config.visConfig.enable3d,
        nullValue: 0,
        getAttributeValue: () => (d: any) => d.properties?.elevation || defaultElevation,
        supportedFieldTypes: numericFieldTypes
      },
      radius: {
        property: 'radius',
        field: 'radiusField',
        scale: 'radiusScale',
        domain: 'radiusDomain',
        range: 'radiusRange',
        key: 'radius',
        channelScaleType: CHANNEL_SCALES.radius,
        accessor: 'getPointRadius',
        supportedFieldTypes: numericFieldTypes,
        nullValue: 0,
        getAttributeValue: () => (d: any) => d.properties.radius || defaultRadius,
        aggregation: 'radiusAggregation'
      },
      customMarkers: {
        property: 'customMarkers',
        key: 'customMarkers',
        field: 'customMarkersField',
        scale: 'customMarkersScale',
        domain: 'customMarkersDomain',
        range: 'customMarkersRange',
        fixed: 'customMarkersUrl',
        channelScaleType: CHANNEL_SCALES.customMarker,
        accessor: 'getIconUrl',
        supportedFieldTypes: ['string'],
        getAttributeValue: (config) => config.visConfig.customMarkersUrl,
        condition: (config) => config.visConfig.customMarkers
      },
      rotation: {
        property: 'rotation',
        key: 'rotation',
        field: 'rotationField',
        domain: 'rotationDomain',
        scale: 'rotationScale',
        channelScaleType: CHANNEL_SCALES.identity,
        accessor: 'getRotation',
        supportedFieldTypes: numericFieldTypes,
        defaultValue: () => 0,
        nullValue: 0,
        condition: (config) => config.rotationField
      }
    }
  }

  static findDefaultLayerProps(dataset: KeplerTable, foundLayers) {
    const emptyProps = { props: [] }

    if (foundLayers?.length) {
      return emptyProps
    }

    const scheme = getSchemeFromTilesetDataset(dataset)

    if (getDatasetType(dataset) === MapType.tileset && (!scheme || scheme === 'xyz')) {
      return {
        props: [
          {
            label: dataset.label,
            isVisible: true,
            columns: {}
          }
        ]
      }
    }
    return emptyProps
  }

  getDefaultLayerConfig(props = {}): any {
    const baseConfig = super.getDefaultLayerConfig(props)
    return {
      ...baseConfig,
      initialLayerColor: baseConfig.color,
      // add height visual channel
      heightField: null,
      heightDomain: [0, 1],
      heightScale: 'linear',

      // add radius visual channel
      radiusField: null,
      radiusDomain: [0, 1],
      radiusScale: 'linear',

      // add stroke color visual channel
      strokeColorField: null,
      strokeColorDomain: [0, 1],
      strokeColorScale: 'quantile',

      // add highlight color
      highlightColor: [...LAYER_HIGHLIGHT_FILL_COLOR_HOVER]
    }
  }

  /**
   * Used to copy config from old layer, when converting layer type.
   *
   * @param configToCopy old layer instance config
   */
  assignConfigToLayer(configToCopy: KeplerLayerConfig) {
    const {
      dataId,
      color,
      columns,
      animation,
      isVisible,
      label,
      isConfigActive,
      visConfig: oldVisConfig
    } = configToCopy
    const { filled = true } = oldVisConfig
    const visConfig = { ...this.config.visConfig, filled }
    this.updateLayerConfig({
      dataId,
      color,
      columns,
      animation,
      isVisible,
      label,
      isConfigActive,
      visConfig
    })
    Object.keys(this.visualChannels).forEach((channel) => {
      this.validateVisualChannel(channel)
    })
  }

  getHoverData(object: any) {
    if (!object) {
      return null
    }

    return object.properties
  }

  neededAttributesAreAvailable(dataset: KeplerTable) {
    const attributes = getAttributesFromTilesetDataset(dataset)

    return Object.keys(this.visualChannels).every((channel) => {
      const { field: fieldName, channelScaleType } = this.visualChannels[channel]
      if (channelScaleType === 'identity') return true

      const field = this.config[fieldName]
      if (!field) return true

      const attribute = attributes.find((attr) => attr.attribute === field.id)
      return attributeHasStats(attribute)
    })
  }

  get noneLayerDataAffectingProps() {
    return this.useOpacityPerChannel ? noneLayerDataAffectingPropsWithOpacityForChannel : noneLayerDataAffectingProps
  }

  // still keeping this to setup state properly
  // should be removed piece by piece
  formatLayerData(datasets: any, oldLayerData: any) {
    const dataset = datasets[this.config.dataId]

    // Dynamic tiling  loads attributes stats on demand so we return the previous layer configuration
    // while the request is resolved if any attribute is necessary to style and is not available yet
    if (!this.neededAttributesAreAvailable(dataset)) {
      return oldLayerData
    }
    const data = getTilejsonFromTilesetDataset(dataset)
    if (!data) {
      return null
    }

    const formatTiles = new URL(data.tiles[0]).searchParams.get('formatTiles') || TILE_FORMATS.MVT

    const bounds = getBoundsFromTilesetDataset(dataset)
    const geometry = getGeometryFromTilesetDataset(dataset)
    const featureTypes = geometry && { [geometry.toLocaleLowerCase()]: true }
    const geoColumn = dataset.metadata?.geoColumn
    const scheme = dataset.metadata?.scheme
    const schema = dataset.metadata?.schema
    const sourceType = dataset.metadata?.sourceType
    const provider = dataset.metadata?.provider

    this.updateMeta({
      ...(featureTypes && { featureTypes }),
      ...(bounds && { bounds }),
      formatTiles,
      geoColumn,
      scheme,
      schema,
      sourceType,
      provider
    })

    this.updateHexColorDomain(dataset)
    this.updateColorMaps()

    return null
  }

  hasLayerData(layerData) {
    return Boolean(layerData)
  }

  /**
   * This creates config for  _newly_ added layer.
   *
   * In contrast to Layer.getDefaultLayerConfig, which is called several
   * times during the layer lifecycle.
   */
  getNewLayerConfig(dataset: KeplerTable, otherLayers: KeplerLayer[]): Partial<KeplerLayerConfig> {
    const geometry = getGeometryFromTilesetDataset(dataset)
    const colorGenerator = createNewLayerColorGenerator(
      otherLayers.filter((layer) => layer !== (this as unknown as KeplerLayer))
    )
    const color = colorGenerator()
    const strokeColor = colorGenerator()
    const stroked = geometry ? strokedGeometries.includes(geometry) : false

    return {
      color,
      visConfig: {
        ...this.config.visConfig,
        filled: true,
        stroked,
        strokeColor,
        initialStrokeColor: strokeColor
      }
    }
  }

  // ok, this updates ColorRange for hex color config, so it's properly
  // rendered in "ColorRangeSelector" in layer panel :/ MAN!, this should be implemented in different way ...
  // TODO ColorRangeSelector _with_ hex color column should actually dynamically load colors from stats ...
  updateHexColorDomain(dataset: KeplerTable) {
    Object.values(this.visualChannels as Record<string, KeplerVisualChannel>).forEach((channel) => {
      const colorRange: KeplerColorRange | undefined = this.config.visConfig[channel.range]
      const field: KeplerTableField | undefined = this.config[channel.field]

      if (!colorRange?.hexColor || !field?.colorColumn) return

      const attribute = getHexColumnStatsFromTilesetDataset(dataset, field.colorColumn)
      if (attribute && 'categories' in attribute) {
        const colors = attribute?.categories?.map(({ category }) => checkColorOrFallback(category))
        this.updateLayerVisConfig({ [channel.range]: { ...colorRange, colors } })
      }
    })

    return this
  }

  /**
   * Some colorMaps* may be outdated, as they are _selected_ synchronously when changing scale type,
   * but domain is not yet available. This method updates colorMaps for scales that need it.
   *
   * * scale === custom` && `range.uiCustomScaleType` === 'logarithmic'
   */
  updateColorMaps() {
    const visConfig = this.config.visConfig
    let newVisConfig = visConfig

    for (const channel of Object.values(this.visualChannels) as KeplerVisualChannel[]) {
      const scale: KeplerScaleType | undefined = this.config[channel.scale]
      const colorRange: KeplerColorRange | undefined = this.config.visConfig[channel.range]
      const field: KeplerTableField | undefined = this.config[channel.field]
      const domain = this.config[channel.domain]

      if (!scale || !colorRange || !field) continue

      if (scale === KeplerScaleType.Custom && colorRange.uiCustomScaleType === 'logarithmic') {
        const log10ScaleColorMap = getLog10ScaleColorMap({
          min: domain[0],
          max: domain[1],
          colors: colorRange.colors
        })
        if (!colorRange.colorMap || !colorMapsEqual(colorRange.colorMap, log10ScaleColorMap)) {
          newVisConfig = {
            ...newVisConfig,
            [channel.range]: {
              ...colorRange,
              colorMap: log10ScaleColorMap
            }
          }
        }
      }
    }
    if (newVisConfig !== visConfig) {
      this.updateLayerVisConfig(newVisConfig)
    }
  }

  calculateLayerDomain(dataset: any, visualChannel: any) {
    const defaultDomain = [0, 1]
    const { scale, aggregation } = visualChannel
    const scaleType = this.config[scale]
    const aggregationType = aggregation && this.config.visConfig[aggregation]

    const field = this.config[visualChannel.field]
    if (!field) {
      return defaultDomain
    }

    const attribute: any = getAttributesFromTilesetDataset(dataset).find((a: any) => a.attribute === field.name)
    const steps = this.config.visConfig[visualChannel.range]?.colors?.length

    if (aggregationType === AGGREGATION_TYPES.count) {
      return [0, Infinity]
    }
    if (attribute && attributeHasStats(attribute)) {
      switch (scaleType) {
        case SCALE_TYPES.ordinal:
        case SCALE_TYPES.point:
          return attribute.categories
            .map((c: { category: string }) => c.category)
            .filter((c: string) => c !== undefined && c !== null)

        case SCALE_TYPES.quantile:
          return attribute.quantiles[steps]

        case SCALE_TYPES.log: {
          const [d0, d1] = [attribute.min, attribute.max]
          return [d0 === 0 ? 1e-5 : d0, d1]
        }

        case SCALE_TYPES.quantize:
        case SCALE_TYPES.linear:
        case SCALE_TYPES.sqrt:
        default:
          return [attribute.min, attribute.max]
      }
    }

    return defaultDomain
  }

  // TODO:
  // This should be a saga that once it observes layer with hex column, it ensures
  // that stats are loaded into CACHE
  // legends should use this CACHE and trigger this load instead of rendering layer
  async calculateHexColumnLegend(datasets: Record<string, KeplerTable>) {
    const legends = {}
    const dataset: KeplerTable | undefined = this.getDataset(datasets)
    if (!dataset || dataset.metadata.sourceType === MapType.tileset) {
      return
    }

    for (const channel of Object.keys(this.visualChannels)) {
      const { range, field: channelField } = this.visualChannels[channel]
      const colorRange: KeplerColorRange | undefined = this.config.visConfig[range]
      const field: KeplerTableField | undefined = this.config[channelField]

      if (colorRange?.hexColor && field?.colorColumn) {
        const response = await getLegendSettings(dataset, field)

        if (response) {
          legends[channel] = response.rows.map((row) => ({ ...row, color: checkColorOrFallback(row.color) }))
        }
      }
    }

    this.updateLayerLegend(legends)
  }

  renderLayer(): never {
    throw new Error('Not implemented')
  }

  async getPickedFeatures({
    widgetSource,
    featureIds,
    columns,
    zoom,
    spatialFilter
  }: {
    widgetSource: WidgetSource
    featureIds: string[]
    columns: string[]
    zoom: number
    spatialFilter?: SpatialFilter
  }): Promise<Record<string, unknown>[]> {
    const spatialIndex = this.geoColumn ? getSpatialIndexFromGeoColumn(this.geoColumn) : undefined
    const tileResolution = getDynamicTileResolution(spatialIndex)

    let dataType: GeomType
    if (this.meta.featureTypes?.point) {
      dataType = GeomType.Points
    } else if (this.meta.featureTypes?.line || this.meta.featureTypes?.linestring) {
      dataType = GeomType.Lines
    } else if (this.meta.featureTypes?.polygon) {
      dataType = GeomType.Polygons
    } else {
      throw new Error('Cannot pick empty layer')
    }

    const { rows } = await widgetSource.getFeatures({
      columns,
      featureIds,
      dataType,
      spatialFilter,
      tileResolution,
      z: zoom
    })

    return rows
  }
}
